/**
 * What the clinic reads when the assistant stops a turn ON PURPOSE (SCRUM-300).
 *
 * Keyed by the harness spec section 15 reason code the runtime emits on the
 * `data-paused` stream part — never by parsing the prose, which would break the
 * day the sentence is reworded and would couple the UI to the runtime's wording.
 *
 * Every entry says three things, because PRODUCT_GOVERNANCE rule 8 asks for all
 * three: the turn stopped, why it stopped, and what the clinic can do next.
 * None of them blames an outage: the assistant did not break, it reached a
 * ceiling. No clinic screen edits the AI cap, so the daily and monthly limits
 * point to CliniSharp support rather than to a setting (COPILOT-COPY-01).
 */
export type PausedCopy = {
  title: string;
  body: string;
};

const PAUSED_COPY = {
  budget_steps: {
    title: "Parei nesta etapa",
    body: "Essa tarefa passou do número de etapas previsto para uma resposta. O que consegui levantar está acima. Peça de novo em partes menores para continuar.",
  },
  budget_tool_calls: {
    title: "Parei nesta etapa",
    body: "Essa tarefa precisou de mais consultas do que o previsto para uma resposta. O que consegui levantar está acima. Peça o próximo passo para eu continuar.",
  },
  budget_tokens: {
    title: "Conversa muito longa",
    body: "Esta conversa chegou ao limite de texto de uma resposta. O que consegui levantar está acima. Comece um assunto novo para continuar.",
  },
  budget_usd: {
    title: "Parei nesta etapa",
    body: "Essa tarefa chegou ao custo máximo previsto para uma resposta. O que consegui levantar está acima. Peça de novo em partes menores para continuar.",
  },
  budget_daily: {
    title: "Assistente pausado hoje",
    body: "O limite diário de uso de IA da clínica foi atingido. O assistente volta depois da renovação do limite. Para ampliar o limite, fale com o suporte da CliniSharp.",
  },
  budget_monthly: {
    title: "Assistente pausado neste mês",
    body: "O limite mensal de uso de IA da clínica foi atingido. O assistente volta no início do próximo mês. Para ampliar o limite, fale com o suporte da CliniSharp.",
  },
  policy_unavailable: {
    title: "Não consegui confirmar agora",
    body: "Não foi possível confirmar as permissões de uso da clínica, então não segui adiante. Tente de novo em instantes.",
  },
} as const satisfies Record<string, PausedCopy>;

/**
 * The fallback is STATED, never an empty panel. A reason code this build does
 * not know is a runtime ahead of the app, and the clinic is told exactly that —
 * without inferring a business effect from a runtime pause.
 */
const UNKNOWN: PausedCopy = {
  title: "Assistente pausado",
  body: "O assistente parou esta tarefa e o motivo não foi reconhecido por esta versão do app. Confira o resultado das ações antes de tentar novamente ou fale com o suporte.",
};

/** Copy for a reason code, with a stated fallback — never an empty panel. */
export function pausedCopyFor(reasonCode: string): PausedCopy {
  return (PAUSED_COPY as Record<string, PausedCopy>)[reasonCode] ?? UNKNOWN;
}

/** The reason codes this build renders bespoke copy for. Exported for the test. */
export const KNOWN_PAUSED_REASON_CODES = Object.keys(PAUSED_COPY);
